import {Injectable} from "@angular/core";
import {Observable, Subject} from "rxjs";
import {HttpService} from "../technique/http.service";
import {ResponseWrapper} from "../../suppliers/wrappers/response-wrapper";
import {FiltersContextOrganization} from "../../dtos/filters-context-organization-dto";
import {InfosRoundDto} from "../../dtos/infos-round-dto";
import {MSG_KEY, MSG_SEVERITY} from "../../constants";
import {ProduitDeclinaisonDTO} from "../../dtos/produit-declinaison-dto";
import {ContratMenuConviveDTO} from "../../dtos/contratmenuconvive-dto";
import {RepasDTO} from "../../dtos/repas-dto";
import {Tournee} from "../../dtos/tournee-dto";
import {PointDeLivraisonDTO} from "../../dtos/point-de-livraison-d-t-o";
import {ToastService} from "../technique/toast.service";
import {GraphQLService} from "../technique/graphql.service";
import {OrganisationTourneeInfoDto} from "../../dtos/organisation-tournee-info-dto";
import {SiteDTO} from "../../dtos/site-dto";
import {ObjectDTO} from "../../dtos/object-dto";
import {Auth2Service} from "../security/auth2.service";
import {catchError} from "rxjs/operators";
import {UtilsService} from "../../utils/utils.service";

@Injectable({
  providedIn: 'root'
})
export class OrganisationsTourneesService {

  private subjectOpenDialog = new Subject<OrganisationTourneeInfoDto>();
  openDialogEdition$ = this.subjectOpenDialog.asObservable();

  private subjectRefreshList = new Subject();
  refreshList$ = this.subjectRefreshList.asObservable();

  constructor(private auth2Svc: Auth2Service,
              private httpSvc: HttpService,
              private toastSvc: ToastService,
              private graphQlSvc: GraphQLService,
              private utils: UtilsService) {
  }

  canModify(element: ObjectDTO): boolean {
    if (!element || !element?.site || !element?.site.id)
      return true;
    return this.auth2Svc.isSiteLocal(element.site.id);
  }

  deleteOrganizationsByFilters = (rowsToRemove: number[]): Observable<ResponseWrapper<boolean>> => {
    let params: string = '?1=1';
    if (rowsToRemove?.length) params += `&idsToDelete=${rowsToRemove.join(',')}`
    return this.httpSvc.delete(`dolrest/tournees/organisations/delete${params}`);
  }

  fetchFiltersContext = (): Observable<ResponseWrapper<FiltersContextOrganization>> => this.httpSvc.get(`dolrest/tournees/organisations/filtersContext`);

  announceOpenEditionDialog = (orga?: OrganisationTourneeInfoDto) => this.subjectOpenDialog.next(orga);

  announceRefreshList = () => this.subjectRefreshList.next();

  save = (organisationTournees: OrganisationTourneeInfoDto, onlyCheck: boolean): Observable<ResponseWrapper<any>> => {
    const copyOrganization = this.copyOrganizationforSave(organisationTournees);
    return this.httpSvc.post(`dolrest/tournees/organisations/save/${onlyCheck}`, copyOrganization);
  }

  copyOrganizationforSave = (organisationTournees: OrganisationTourneeInfoDto): OrganisationTourneeInfoDto => {
    let copyOrganization: OrganisationTourneeInfoDto = JSON.parse(JSON.stringify(organisationTournees));

    copyOrganization.site = {id: copyOrganization?.site?.id} as SiteDTO;
    Object.keys(copyOrganization?.informationsByDeliveryDay).forEach(dayDelivery => {
      copyOrganization?.informationsByDeliveryDay[dayDelivery].forEach(row => {
        row.pointLivraison = {id: row.pointLivraison.id, libelle: row.pointLivraison.libelle} as PointDeLivraisonDTO;

        if (row?.famillesPlats?.length) {
          row.famillesPlats = row.famillesPlats.map(f => { delete(f.selected); delete(f.expanded); return f; });
        }

        if (row?.prestation?.libelle === 'Toutes' && !row?.prestation?.id)
          row.prestation = null;
        if (row?.famillesPlats?.find(fp => !fp.id && fp.libelle === 'Toutes'))
          row.famillesPlats = null;
        if (row?.plats?.find(p => !p.id && p.code === 'Tous'))
          row.plats = null;
      });
    });
    return copyOrganization;
  }

  findAll = (idsSite: number[]) => {
    let params: string = '?1=1';
    if (idsSite?.length) params += `&idsSite=${idsSite.join(',')}`
    return this.httpSvc.get(`dolrest/tournees/organisations/all${params}`);
  }

  prepareRow = (form: FormInfosRound, callbackSuccess: any): void => {
    form?.jourConsoSelected.forEach(jourConso => {
      form?.repasSelected.forEach(repas => {
        if (form?.prestationsSelected.length) {
          form?.prestationsSelected.forEach(cmc => {
            let info: InfosRoundDto = this.initializeInfosRound(jourConso, repas, form);
            info.prestation = cmc;
            this.prepareFamillesAndPlatsForPrestation(info, form?.famillesPlats, form?.codesPlats);
            callbackSuccess(form?.jourLivSelected.toUpperCase(), info);
          });
        } else {
          let info: InfosRoundDto  = this.initializeInfosRound(jourConso, repas, form);
          info.prestation = { id: null, libelle: 'Toutes' } as ContratMenuConviveDTO;
          this.prepareFamillesAndPlatsForPrestation(info, form?.famillesPlats, form?.codesPlats);
          callbackSuccess(form?.jourLivSelected.toUpperCase(), info);
        }
      });
    });
  }

  private initializeInfosRound = (jourConso: string, repas: RepasDTO, form: FormInfosRound): InfosRoundDto => {
    let info: InfosRoundDto  = new InfosRoundDto();
    info.tournee = form?.tourneeSelected;
    info.order = form?.orderSelected || 1;
    info.pointLivraison = form?.plcSelected
    info.deliveryDay = form?.jourLivSelected.toUpperCase();
    info.consumptionDay = jourConso.toUpperCase();
    info.repas = repas;
    return info;
  }

  private prepareFamillesAndPlatsForPrestation = (info: InfosRoundDto, famillesPlats: any[], codesPlats: string): void => {
    if (famillesPlats && famillesPlats.length)
      info.famillesPlats = famillesPlats;

    if (codesPlats) {
      this.checkExistenceCodes(codesPlats, (platsNotFounds) => {
        info.plats = info.plats.filter(p => !platsNotFounds.find(e => e === p.code));
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.WARNING, `Les codes de plats suivants sont inconnus : ${platsNotFounds.map(p => p.code).join(",")}`);
      }, (plats: ProduitDeclinaisonDTO[]) => { info.plats = plats });
    }
    if (!info.famillesPlats || !info.famillesPlats.length)
      info.famillesPlats = [{ id: null, libelle: 'Toutes'} as any];
    if (!info.plats || !info.plats.length)
      info.plats = [{ id: null, code: 'Tous'} as ProduitDeclinaisonDTO];
  }

  private checkExistenceCodes = (codesPlats: string, callBackPlatsUnfound: any, callBackPlatsSuccess: any): void => {
    const plats: ProduitDeclinaisonDTO[] = codesPlats.split(',').map(code => ({ code }) as ProduitDeclinaisonDTO);
    this.graphQlSvc.sendQuery(`
                {
                  allProduitsDeclines(filters: {
                    codes: [${plats.map(it => `"${it.code}"`)}]
                  }) {
                      id,
                      libelle,
                      code
                  }
                }
              `).toPromise()
      .then(response => {
        let produitsDeclinesFound: ProduitDeclinaisonDTO[] = response.allProduitsDeclines;
        let errorsPlatsUnfound: string[] = [];
        plats.forEach(plat => {
          const platFound: ProduitDeclinaisonDTO = produitsDeclinesFound.find(pd => pd.code === plat.code);
          if (platFound)
            plat.id = platFound.id;
          else
            errorsPlatsUnfound.push(`${plat.code}`);
        });
        if (errorsPlatsUnfound)
          callBackPlatsUnfound(plats);
        else
          callBackPlatsSuccess(plats);
      });
  }

  duplicateOrganisationTournee = (organisationToDuplicate: OrganisationTourneeInfoDto): Observable<ResponseWrapper<any>> => {
    const copyOrganisationToDuplicate = this.copyOrganizationforSave(organisationToDuplicate)
    return this.httpSvc.post(`dolrest/tournees/organisations/duplicate`, copyOrganisationToDuplicate)
      .pipe(catchError(error => this.utils.handleError(error, true)));
  }

}

export interface FormInfosRound {
  tourneeSelected: Tournee;
  orderSelected: number;
  plcSelected: PointDeLivraisonDTO;
  jourLivSelected: string;
  jourConsoSelected: string[];
  repasSelected: RepasDTO[];
  prestationsSelected: ContratMenuConviveDTO[];
  famillesPlats: any[];

  codesPlats: string;
}
